import { useCallback, useEffect, useRef, useState } from 'react'
import useWindowSize from '../WebcamCapture/hooks/useWindowSize'
import useAudioSpectrum from './useAudioSpectrum'
import { IconButton, Text } from '@yes.technology/react-toolkit'
import { Container, PlayerBar, ProgressBar, ProgressBarWrapper } from './styles'

type PlayerVisualizerProps = {
  url?: string
  variant?: 'primary' | 'secondary'
}

type StyleScheme = {
  backgroundColor: string
  barsColor: string
  fontColor: string
  barWidth?: number
  barGap?: number
}

const STYLE_SCHEME: Record<string, StyleScheme> = {
  primary: {
    backgroundColor: '#ffffff',
    barsColor: '#096EB6',
    fontColor: '#555'
  },
  secondary: {
    backgroundColor: '#042C49',
    barsColor: '#096EB6',
    fontColor: '#ffffff',
    barWidth: 10,
    barGap: 10
  }
}

const PlayerVisualizer = ({
  url,
  variant = 'primary'
}: PlayerVisualizerProps) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const animationRef = useRef<number | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)

  const size = useWindowSize()

  const { drawSpectrum } = useAudioSpectrum({
    canvasRef,
    analyserRef,
    animationRef,
    backgroundColor: STYLE_SCHEME[variant].backgroundColor,
    barColor: STYLE_SCHEME[variant].barsColor,
    barGap: STYLE_SCHEME[variant].barGap,
    barWidth: STYLE_SCHEME[variant].barWidth
  })

  useEffect(() => {
    return () => {
      stopPlayerVisualizer()
      audioContextRef.current?.close()
    }
  }, [])

  useEffect(() => {
    if (url) drawSpectrum()
  }, [url, drawSpectrum])

  const handlePlayPause = useCallback(async () => {
    const audio = audioRef.current
    if (!audio) return

    if (!audioContextRef.current) {
      const audioCtx = new AudioContext()
      audioContextRef.current = audioCtx
      const source = audioCtx.createMediaElementSource(audio)
      const analyser = audioCtx.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      analyser.connect(audioCtx.destination)
      analyserRef.current = analyser
      drawSpectrum()
    }

    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume()
    }

    if (audio.paused) {
      audio
        .play()
        .then(() => {
          setIsPlaying(true)
          if (!animationRef.current) drawSpectrum()
        })
        .catch(() => setIsPlaying(false))
    } else {
      audio.pause()
      setIsPlaying(false)
    }
  }, [drawSpectrum])

  const stopPlayerVisualizer = () => {
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }
  }

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || !duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * duration
  }

  return (
    <Container backgroundColor={STYLE_SCHEME[variant].backgroundColor}>
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
        onEnded={() => setIsPlaying(false)}
        style={{ display: 'none' }}
      />
      <canvas
        ref={canvasRef}
        width={size.width}
        height={Math.max(150, (size.height || 0) - 310)}
        style={{ width: '100%', flex: 1, display: 'block' }}
      />
      <PlayerBar backgroundColor={STYLE_SCHEME[variant].backgroundColor}>
        <IconButton
          onClick={handlePlayPause}
          iconName={isPlaying ? 'Pause' : 'Play'}
          backgroundColor={STYLE_SCHEME[variant].backgroundColor}
          textColor={variant === 'primary' ? 'black' : 'white'}
          containerStyles={{
            width: 40,
            height: 40
          }}
        />
        <ProgressBarWrapper onClick={handleSeek}>
          <ProgressBar width={duration ? (currentTime / duration) * 100 : 0} />
        </ProgressBarWrapper>
        <Text
          variant='content-01'
          style={{
            color: STYLE_SCHEME[variant].fontColor
          }}
        >
          {formatTime(currentTime)} / {formatTime(duration)}
        </Text>
      </PlayerBar>
    </Container>
  )
}

export default PlayerVisualizer
